import { useEffect, useState } from "react";

/**
 * Mounts children only after the browser is idle (or after a short timeout /
 * first user interaction). Keeps non-critical widgets — chatbot, location
 * modal — out of the initial render and off the LCP critical path without
 * removing any functionality.
 */
export function DeferredMount({
  children,
  timeout = 2500,
}: {
  children: React.ReactNode;
  timeout?: number;
}) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready) return;
    let cancelled = false;
    const show = () => {
      if (!cancelled) setReady(true);
    };

    const events: Array<keyof WindowEventMap> = ["pointerdown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, show, { once: true, passive: true }));

    const idle = (window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    }).requestIdleCallback;
    const timer = idle ? idle(show, { timeout }) : window.setTimeout(show, timeout);

    return () => {
      cancelled = true;
      events.forEach((e) => window.removeEventListener(e, show));
      if (!idle) window.clearTimeout(timer as number);
    };
  }, [ready, timeout]);

  return ready ? <>{children}</> : null;
}
